/**
 * Conditions Portal Types
 *
 * Types for the secure, token-based portal where recipients can:
 * - View loan conditions
 * - Download approval letter PDF
 * - Download condition template forms
 * - Upload documents addressing conditions
 *
 * Portal is accessed by processors, loan officers, borrowers, or agents.
 */

import type { ApprovalLetterData } from './approval-letter';

// ============================================
// TOKEN TYPES
// ============================================

export type RecipientType = 'borrower' | 'co_borrower' | 'partner' | 'title_escrow';
/** Portal access type - includes RecipientType plus 'internal' for authenticated staff access */
export type PortalAccessType = RecipientType | 'internal';
export type DeviceType = 'desktop' | 'mobile' | 'tablet' | 'unknown';
export type EncompassSyncStatus = 'pending' | 'syncing' | 'synced' | 'failed';

/**
 * Token stored in database
 */
export interface ConditionsPortalToken {
  id: string;
  encompassLoanId: string;
  loanNumber: string | null;
  token: string;
  recipientEmail: string;
  recipientName: string;
  recipientType: RecipientType;
  createdByUserId: string | null;
  expiresAt: string;
  firstViewedAt: string | null;
  lastViewedAt: string | null;
  viewCount: number;
  isActive: boolean;
  revokedAt: string | null;
  revokedReason: string | null;
  revokedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Token from database (snake_case)
 */
export interface ConditionsPortalTokenRow {
  id: string;
  encompass_loan_id: string;
  loan_number: string | null;
  token: string;
  recipient_email: string;
  recipient_name: string;
  recipient_type: RecipientType;
  created_by_user_id: string | null;
  expires_at: string;
  first_viewed_at: string | null;
  last_viewed_at: string | null;
  view_count: number;
  is_active: boolean;
  revoked_at: string | null;
  revoked_reason: string | null;
  revoked_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================
// VIEW EVENT TYPES
// ============================================

export interface ConditionsPortalViewEvent {
  id: string;
  tokenId: string;
  viewedAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  deviceType: DeviceType | null;
  success: boolean;
  failureReason: string | null;
}

// ============================================
// UPLOAD TYPES
// ============================================

export interface ConditionsPortalUpload {
  id: string;
  tokenId: string | null; // Nullable for internal uploads
  encompassLoanId: string | null; // For internal uploads without token
  uploadedByUserId: string | null; // For internal uploads, the user who uploaded
  conditionExternalId: string;
  conditionName: string | null;
  originalFilename: string;
  sanitizedFilename: string;
  storagePath: string;
  fileSizeBytes: number;
  mimeType: string;
  encompassSyncStatus: EncompassSyncStatus;
  encompassDocumentId: string | null;
  encompassSyncAttempts: number;
  encompassLastError: string | null;
  encompassSyncedAt: string | null;
  processingNotifiedAt: string | null;
  uploadedAt: string;
  ipAddress: string | null;
  userAgent: string | null;
}

export interface ConditionsPortalUploadRow {
  id: string;
  token_id: string | null; // Nullable for internal uploads
  encompass_loan_id: string | null; // For internal uploads without token
  uploaded_by_user_id: string | null; // For internal uploads, the user who uploaded
  condition_external_id: string;
  condition_name: string | null;
  original_filename: string;
  sanitized_filename: string;
  storage_path: string;
  file_size_bytes: number;
  mime_type: string;
  encompass_sync_status: EncompassSyncStatus;
  encompass_document_id: string | null;
  encompass_sync_attempts: number;
  encompass_last_error: string | null;
  encompass_synced_at: string | null;
  processing_notified_at: string | null;
  uploaded_at: string;
  ip_address: string | null;
  user_agent: string | null;
}

// ============================================
// LOAN TEAM CONTACTS (Processor, Underwriter)
// ============================================

export interface LoanTeamContact {
  name: string;
  email: string;
  phone: string;
  roleId?: string;
  photoUrl?: string | null;
}

// ============================================
// LOAN ASSOCIATES (For Bulk Reassignment)
// ============================================

/**
 * Loan associate from Encompass API
 * Used for bulk condition reassignment dropdown
 */
export interface LoanAssociate {
  id: string;
  entityId: string; // Same as id, for consistency with condition owner structure
  name: string;
  email: string;
  roleName: string;
  roleId: string;
  phone: string;
}

/**
 * Condition owner structure (matches Encompass API format)
 */
export interface ConditionOwner {
  entityId: string;
  entityName: string;
  entityType: string; // Usually 'User'
}

// ============================================
// TITLE & ESCROW INFO
// ============================================

export interface TitleEscrowInfo {
  companyName: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

// ============================================
// PORTAL DOCUMENTS (For Title/Escrow)
// ============================================

/**
 * Document source type for portal attachments
 */
export type PortalDocumentSource = 'encompass' | 'upload';

/**
 * Document attached to a title/escrow portal (returned from API)
 */
export interface PortalDocument {
  id: string;
  source: PortalDocumentSource;
  displayName: string;
  fileSize: number | null;
  mimeType: string | null;
  attachedAt: string;
  /** Storage path for generating download URL */
  storagePath?: string;
  /** For Encompass docs, the attachment ID */
  encompassAttachmentId?: string;
}

/**
 * Document selected during assignment flow (client-side)
 * Used in TitleEscrowAssignModal before submission
 */
export interface SelectedDocumentForAttachment {
  /** Unique ID for tracking in the UI */
  id: string;
  source: PortalDocumentSource;
  /** For Encompass docs: the attachment ID */
  attachmentId?: string;
  /** For uploads: the File object (client-side only, not sent to API) */
  file?: File;
  /** Display name shown in UI */
  displayName: string;
  fileSize?: number;
  mimeType?: string;
  /** Storage path (set after upload completes) */
  storagePath?: string;
}

/**
 * Database row for title_escrow_portal_documents (snake_case)
 */
export interface TitleEscrowPortalDocumentRow {
  id: string;
  portal_token_id: string;
  source: PortalDocumentSource;
  encompass_attachment_id: string | null;
  original_filename: string | null;
  sanitized_filename: string | null;
  storage_path: string | null;
  file_size_bytes: number | null;
  mime_type: string | null;
  encompass_sync_status: EncompassSyncStatus;
  encompass_synced_attachment_id: string | null;
  encompass_sync_error: string | null;
  encompass_synced_at: string | null;
  display_name: string;
  attached_by_user_id: string | null;
  attached_at: string;
  created_at: string;
  updated_at: string;
}

// ============================================
// LOAN OFFICER INFO
// ============================================

export interface ConditionsPortalLoanOfficer {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  mobilePhone: string | null;
  nmls: string | null;
  title: string | null;
  photoUrl: string | null;
}

// ============================================
// COMPANY INFO
// ============================================

export interface ConditionsPortalCompany {
  name: string;
  legalName: string | null;
  logoUrl: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  nmls: string | null;
  address: {
    street: string | null;
    suite: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
  } | null;
  equalHousingLender: boolean;
}

// ============================================
// CONDITION TEMPLATE INFO
// ============================================

export interface ConditionTemplateInfo {
  externalId: string;
  templateName: string;
  hasTemplate: boolean;
}

// ============================================
// PORTAL DATA (Returned by validate action)
// ============================================

/**
 * Extended loan data for conditions portal
 * Includes processor/underwriter contact info from Encompass associates API
 * and title/escrow company contact information
 */
export interface MilestoneHistoryItem {
  id?: string;
  name: string;
  doneDate?: string | null;
  startDate?: string | null;
  expectedDate?: string | null;
  doneBy?: string | null;
  comments?: string | null;
  duration?: number | null;
  done?: boolean;
}

export interface ConditionsPortalLoanData extends ApprovalLetterData {
  processor: LoanTeamContact | null;
  underwriter: LoanTeamContact | null;
  titleEscrow: TitleEscrowInfo | null;
  milestoneHistory?: MilestoneHistoryItem[];
  currentMilestone?: string | null;
  isFileLocked?: boolean;
  fileLockedByName?: string | null;
  fileLockTime?: string | null;
}

export interface ConditionsPortalData {
  valid: boolean;
  loanData: ConditionsPortalLoanData;
  loanOfficer: ConditionsPortalLoanOfficer;
  company: ConditionsPortalCompany;
  templates: Record<string, ConditionTemplateInfo>;
  uploads: ConditionsPortalUpload[];
  /** Notes from lender for title/escrow portal conditions (keyed by externalId) */
  conditionNotes?: Record<string, string>;
  /** Documents attached to title/escrow portal for download */
  attachedDocuments?: PortalDocument[];
  /** Formatted deadline for title/escrow document requests (null when no active T/E portal) */
  documentsNeededBy?: string | null;
  /** Additional CC recipients from notification settings */
  additionalCcRecipients?: Array<{
    id: string;
    name: string;
    email: string;
  }>;
  meta: {
    tokenId?: string; // Optional for internal access
    recipientName?: string; // Optional for internal access
    recipientEmail?: string; // Optional for internal access
    recipientType: PortalAccessType; // 'internal' for authenticated staff
    expiresAt?: string; // Optional for internal access
    viewCount?: number; // Optional for internal access
    lastUpdated: string;
  };
}

// ============================================
// API REQUEST/RESPONSE TYPES
// ============================================

/**
 * Actions for the conditions-portal edge function
 * Token-based actions for external access, plus -internal variants for authenticated staff
 */
export type ConditionsPortalAction =
  // Token-based actions (for external title/escrow access)
  | 'validate' | 'get-pdf' | 'get-condition-doc' | 'submit-to-processing'
  | 'assign-title-escrow' | 'get-associates' | 'bulk-reassign' | 'get-portal-document'
  // Internal actions (for authenticated staff)
  | 'validate-internal' | 'get-pdf-internal' | 'get-condition-doc-internal'
  | 'submit-to-processing-internal' | 'assign-title-escrow-internal'
  | 'get-associates-internal' | 'bulk-reassign-internal' | 'get-portal-document-internal'
  | 'assign-borrower-internal';

export interface ConditionsPortalValidateRequest {
  action: 'validate';
  token: string;
}

export interface ConditionsPortalGetPdfRequest {
  action: 'get-pdf';
  token: string;
}

export interface ConditionsPortalGetConditionDocRequest {
  action: 'get-condition-doc';
  token: string;
  externalId: string;
  borrowerPairId?: string;
}

/**
 * Document attachment info for title/escrow assignment request
 */
export interface DocumentAttachmentRequest {
  source: PortalDocumentSource;
  /** For Encompass docs: the attachment ID */
  attachmentId?: string;
  /** Display name for the document */
  displayName: string;
  /** For uploads: storage path after upload completes */
  storagePath?: string;
  fileSize?: number;
  mimeType?: string;
  originalFilename?: string;
}

/**
 * Request to assign conditions to title/escrow and generate their portal link
 */
export interface ConditionsPortalAssignTitleEscrowRequest {
  action: 'assign-title-escrow';
  token: string;
  conditionIds: string[];  // Array of condition external IDs to assign
  /** Documents to attach to the portal */
  documents?: DocumentAttachmentRequest[];
}

/**
 * Request to get a portal document download URL
 */
export interface ConditionsPortalGetPortalDocumentRequest {
  action: 'get-portal-document';
  token: string;
  documentId: string;
}

/**
 * Request to get loan associates for bulk reassignment dropdown
 */
export interface ConditionsPortalGetAssociatesRequest {
  action: 'get-associates';
  token: string;
}

/**
 * Canonical per-condition payload used by both inline and queued reassignment flows.
 * Carries id + note + display fields so the server/worker can render the assignee
 * email without a separate `conditionDetails` structure.
 */
export interface CanonicalCondition {
  conditionId: string;
  note?: string;
  title?: string;
  /** Full condition text (Encompass externalDescription, falling back to internalDescription). */
  description?: string;
  category?: string;
  priorTo?: string;
}

/**
 * Request to bulk reassign conditions to a new owner
 */
export interface ConditionsPortalBulkReassignRequest {
  action: 'bulk-reassign';
  token: string;
  conditions: CanonicalCondition[];
  newOwner: ConditionOwner;
  assigneeEmail?: string;
  assigneeName?: string;
  assigneeRole?: string;
  portalUrl?: string;
  ccEmails?: string[];  // Optional manual CC recipients for notification email
}

/**
 * Response to a bulk reassign request. Returns 'synced' when the Encompass write
 * completed inline, or 'queued' when the file was locked and the write was
 * enqueued for the worker to apply on unlock.
 */
export interface ConditionsPortalBulkReassignResponse {
  success: true;
  status: 'synced' | 'queued';
  queued?: boolean;
  jobId?: string;
  reassignedCount?: number;
  newOwner?: { entityId: string; entityName: string };
  message?: string;
}

export type ConditionsPortalRequest =
  | ConditionsPortalValidateRequest
  | ConditionsPortalGetPdfRequest
  | ConditionsPortalGetConditionDocRequest
  | ConditionsPortalAssignTitleEscrowRequest
  | ConditionsPortalGetAssociatesRequest
  | ConditionsPortalBulkReassignRequest
  | ConditionsPortalGetPortalDocumentRequest;

export interface ConditionsPortalValidateResponse {
  success: true;
  data: ConditionsPortalData;
}

export interface ConditionsPortalPdfResponse {
  success: true;
  downloadUrl: string;
  expiresAt: string;
}

export interface ConditionsPortalErrorResponse {
  success: false;
  error: string;
  code: 'INVALID_TOKEN' | 'EXPIRED_TOKEN' | 'REVOKED_TOKEN' | 'LOAN_CLOSED' | 'RATE_LIMITED' | 'SERVER_ERROR';
}

/**
 * Response when title/escrow conditions are assigned and portal link is generated
 */
export interface ConditionsPortalAssignTitleEscrowResponse {
  success: true;
  assignedCount: number;
  titleEscrowToken: string;
  portalUrl: string;
  escrowOfficerEmail: string | null;
  escrowOfficerName: string | null;
}

/**
 * Response containing loan associates for bulk reassignment
 */
export interface ConditionsPortalGetAssociatesResponse {
  success: true;
  associates: LoanAssociate[];
}

/**
 * Response after bulk reassigning conditions
 */
export interface ConditionsPortalBulkReassignResponse {
  success: true;
  message: string;
  reassignedCount: number;
  newOwner: {
    entityId: string;
    entityName: string;
  };
}

/**
 * Response containing a download URL for a portal document
 */
export interface ConditionsPortalGetPortalDocumentResponse {
  success: true;
  downloadUrl: string;
  document: PortalDocument;
  expiresAt: string;
}

// ============================================
// BORROWER ASSIGNMENT TYPES
// ============================================

export interface ConditionBorrowerAssignment {
  id: string;
  encompass_loan_id: string;
  condition_external_id: string;
  condition_title: string | null;
  note: string | null;
  assigned_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================
// UPLOAD REQUEST/RESPONSE TYPES
// ============================================

export interface ConditionsPortalUploadRequest {
  token: string;
  externalId: string;
  conditionName?: string;
  // File comes as multipart form data
}

export interface ConditionsPortalUploadResponse {
  success: true;
  uploadId: string;
  confirmationNumber: string;
  filename: string;
  syncStatus: EncompassSyncStatus;
  uploadedAt: string;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Convert database row to frontend type
 */
export function tokenRowToToken(row: ConditionsPortalTokenRow): ConditionsPortalToken {
  return {
    id: row.id,
    encompassLoanId: row.encompass_loan_id,
    loanNumber: row.loan_number,
    token: row.token,
    recipientEmail: row.recipient_email,
    recipientName: row.recipient_name,
    recipientType: row.recipient_type,
    createdByUserId: row.created_by_user_id,
    expiresAt: row.expires_at,
    firstViewedAt: row.first_viewed_at,
    lastViewedAt: row.last_viewed_at,
    viewCount: row.view_count,
    isActive: row.is_active,
    revokedAt: row.revoked_at,
    revokedReason: row.revoked_reason,
    revokedByUserId: row.revoked_by_user_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

/**
 * Convert upload row to frontend type
 */
export function uploadRowToUpload(row: ConditionsPortalUploadRow): ConditionsPortalUpload {
  return {
    id: row.id,
    tokenId: row.token_id,
    encompassLoanId: row.encompass_loan_id,
    uploadedByUserId: row.uploaded_by_user_id,
    conditionExternalId: row.condition_external_id,
    conditionName: row.condition_name,
    originalFilename: row.original_filename,
    sanitizedFilename: row.sanitized_filename,
    storagePath: row.storage_path,
    fileSizeBytes: row.file_size_bytes,
    mimeType: row.mime_type,
    encompassSyncStatus: row.encompass_sync_status,
    encompassDocumentId: row.encompass_document_id,
    encompassSyncAttempts: row.encompass_sync_attempts,
    encompassLastError: row.encompass_last_error,
    encompassSyncedAt: row.encompass_synced_at,
    processingNotifiedAt: row.processing_notified_at,
    uploadedAt: row.uploaded_at,
    ipAddress: row.ip_address,
    userAgent: row.user_agent
  };
}

/**
 * Detect device type from user agent
 */
export function detectDeviceType(userAgent: string | null): DeviceType {
  if (!userAgent) {
return 'unknown';
}
  const ua = userAgent.toLowerCase();
  if (/mobile|android|iphone|ipad|ipod|blackberry|windows phone/i.test(ua)) {
    if (/ipad|tablet/i.test(ua)) {
return 'tablet';
}
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Generate a cryptographically secure confirmation number for uploads
 * Uses crypto.getRandomValues instead of Math.random for security
 */
export function generateConfirmationNumber(): string {
  const timestamp = Date.now().toString(36).toUpperCase();
  const randomBytes = new Uint8Array(4);
  crypto.getRandomValues(randomBytes);
  const random = Array.from(randomBytes, b => b.toString(16).padStart(2, '0')).join('').substring(0, 6).toUpperCase();
  return `UP-${timestamp}-${random}`;
}

/**
 * Validate file type by magic bytes
 */
export const ALLOWED_MIME_TYPES = {
  'application/pdf': { magicBytes: [0x25, 0x50, 0x44, 0x46], extension: '.pdf' }, // %PDF
  'image/jpeg': { magicBytes: [0xFF, 0xD8, 0xFF], extension: '.jpg' },
  'image/png': { magicBytes: [0x89, 0x50, 0x4E, 0x47], extension: '.png' }
} as const;

export type AllowedMimeType = keyof typeof ALLOWED_MIME_TYPES;

/**
 * Check if a MIME type is allowed
 */
export function isAllowedMimeType(mimeType: string): mimeType is AllowedMimeType {
  return mimeType in ALLOWED_MIME_TYPES;
}

/**
 * Sanitize filename for storage
 */
export function sanitizeFilename(filename: string): string {
  // Remove path separators and special characters
  let sanitized = filename
    .replace(/[/\\]/g, '') // Remove path separators
    .replace(/[^a-zA-Z0-9._-]/g, '_') // Replace special chars with underscore
    .replace(/_+/g, '_') // Collapse multiple underscores
    .replace(/^[._-]+/, '') // Remove leading special chars
    .substring(0, 100); // Limit length

  // Ensure it has an extension
  if (!sanitized.includes('.')) {
    sanitized = sanitized || 'document';
  }

  return sanitized;
}

/**
 * File size limits
 */
export const FILE_LIMITS = {
  maxSizeBytes: 25 * 1024 * 1024, // 25MB
  maxFilesPerUpload: 10,
  maxFilesPerTokenLifetime: 50,
  maxFilesPerDay: 5
} as const;

/**
 * Token expiration settings
 */
export const TOKEN_SETTINGS = {
  defaultExpirationDays: 90,
  sessionTimeoutMinutes: 15
} as const;
